import type { entities, parsers } from "@fraction/shared";
import type { Attachment } from "src/components/email/AttachmentManager";
import type { ComposeMode } from "src/components/email/EmailComposeModalContent";
import { syncPersistedRecordAcrossWindows } from "src/stores/crossWindowStoreSync";
import { openEmailComposeWindow } from "src/utilities/openEmailComposeWindow";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createDeferredPersistStorage } from "./deferredPersistStorage";

/**
 * A single email compose session. Holds the original open-intent (mode, reply
 * target, pre-fill content, docGen, etc.) plus the live draft fields that the
 * user is editing. Persisted across reloads via IndexedDB so minimized drafts
 * survive navigation and refreshes.
 */
export interface ComposeSessionState {
  composeId: string;
  createdAt: number;
  updatedAt: number;

  // Open intent (how the session was originally opened)
  mode: ComposeMode;
  applicationId?: string;
  /** Optional, persisted alongside applicationId so the modal can render a
   *  badge with the app's identifier without re-fetching when surfaced
   *  from a context that already has it (notifications, agent flows). */
  applicationEid?: string;
  applicantNames?: string;
  replyTo?: entities.Communication;
  replyToCommunicationId?: string;
  initialTo?: string[];
  initialSubject?: string;
  initialBody?: string;
  initialCc?: string[];
  initialAttachments?: Attachment[];
  docGen?: {
    type: parsers.docGen.DocGenDocumentType;
    additionalProps?: Record<string, any>;
    signingAuthority?: Array<{ firstName: string; lastName: string; email: string; title?: string }>;
  };
  /**
   * When true, the session renders as a small "shrunk modal" floating in the
   * top-right rather than a full-size centered modal or a minimized pill. Used
   * by the proposed-communication open-in-modal flow so the user can see other
   * UI behind it while still having the full editor available.
   */
  compact?: boolean;
  /**
   * If this session was opened from a `ProposedCommunication`, this is its id.
   * Used to dedupe — the auto-open-on-mount path checks for an existing
   * session keyed by this field before opening a fresh one.
   */
  proposedCommunicationId?: string;
  /**
   * ISO timestamp of when the source `ProposedCommunication` was created,
   * if this session was opened from one. Surfaced in the modal header so the
   * user knows the LLM's draft has been sitting around — fresher proposals
   * are more relevant than stale ones.
   */
  proposedCommunicationCreatedAt?: string;

  // Live draft (populated as the user types — falls back to the initial/derived
  // values on first mount of the modal).
  draft?: {
    activeMode?: ComposeMode;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body?: string;
    attachments?: Attachment[];
    showCcBcc?: boolean;
    /** Selected send-as / alias address to send from. */
    from?: string;
  };
}

/**
 * In-memory callback registry — `onSent` can't be persisted to IDB (functions
 * aren't serializable). Callers register their callback when they open a
 * session; it's cleared when the session closes.
 */
const onSentCallbacks = new Map<string, (communicationId: string) => void>();

export function registerOnSent(composeId: string, cb: (communicationId: string) => void) {
  onSentCallbacks.set(composeId, cb);
}
export function getOnSent(composeId: string): ((communicationId: string) => void) | undefined {
  return onSentCallbacks.get(composeId);
}
export function clearOnSent(composeId: string) {
  onSentCallbacks.delete(composeId);
}

export type OpenComposeInput = Omit<ComposeSessionState, "composeId" | "createdAt" | "updatedAt" | "draft">;

export interface OpenComposeOptions {
  /**
   * Whether this compose can be handed to its own window. False when something local depends on
   * the send — an `onSent` closure cannot travel to another window — or when the request carries
   * state the window payload has no room for.
   */
  canDetach?: boolean;
}

interface EmailComposeSessionsStore {
  sessions: Record<string, ComposeSessionState>;
  activeComposeId: string | null;

  /**
   * Open a compose. On the desktop it goes to its own window unless the caller says it cannot.
   *
   * Decided here because this is where every compose is born — callers reach it both through
   * `useEmailCompose` and directly, and a rule that lived in the hook would miss the second kind.
   * Returns the session id, or an empty string when a window took it and there is no local session.
   */
  openCompose: (input: OpenComposeInput, options?: OpenComposeOptions) => string;
  focusCompose: (composeId: string) => void;
  minimizeActive: () => void;
  minimizeCompose: (composeId: string) => void;
  closeCompose: (composeId: string) => void;
  updateDraft: (composeId: string, patch: Partial<NonNullable<ComposeSessionState["draft"]>>) => void;
  setCompact: (composeId: string, compact: boolean) => void;
}

function genId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `compose-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Hand a compose to its own window, translating the session's open-intent into the window payload.
 *
 * `mode` does not travel: the window derives a threaded reply from `replyToCommunicationId`, which
 * is the same thing the in-page composer does once it has fetched the communication.
 */
function openComposeInItsOwnWindow(input: OpenComposeInput): boolean {
  return openEmailComposeWindow({
    applicationId: input.applicationId,
    applicationEid: input.applicationEid,
    applicantNames: input.applicantNames,
    to: input.initialTo,
    cc: input.initialCc,
    subject: input.initialSubject,
    body: input.initialBody,
    replyToCommunicationId: input.replyToCommunicationId ?? input.replyTo?.id,
    initialAttachments: input.initialAttachments,
    proposedCommunicationId: input.proposedCommunicationId,
    proposedCommunicationCreatedAt: input.proposedCommunicationCreatedAt,
  });
}

export const useEmailComposeSessionsStore = create<EmailComposeSessionsStore>()(
  persist(
    (set, _get) => ({
      sessions: {},
      activeComposeId: null,

      openCompose: (input, { canDetach = true } = {}) => {
        // A generated document is assembled in this page's session and has nowhere to travel, and a
        // forward is not something the window can rebuild — it derives a threaded reply from
        // `replyToCommunicationId`, which would turn a forward into a reply to the wrong person.
        const detachable = canDetach && input.mode !== "forward" && !input.docGen;
        if (detachable && openComposeInItsOwnWindow(input)) return "";

        const composeId = genId();
        const now = Date.now();
        const session: ComposeSessionState = {
          composeId,
          createdAt: now,
          updatedAt: now,
          ...input,
        };
        set((state) => ({
          sessions: { ...state.sessions, [composeId]: session },
          activeComposeId: composeId,
        }));
        return composeId;
      },

      focusCompose: (composeId) => {
        set((state) => (state.sessions[composeId] ? { activeComposeId: composeId } : state));
      },

      minimizeActive: () => {
        set({ activeComposeId: null });
      },

      minimizeCompose: (composeId) => {
        set((state) => (state.activeComposeId === composeId ? { activeComposeId: null } : state));
      },

      closeCompose: (composeId) => {
        clearOnSent(composeId);
        set((state) => {
          const next = { ...state.sessions };
          delete next[composeId];
          return {
            sessions: next,
            activeComposeId: state.activeComposeId === composeId ? null : state.activeComposeId,
          };
        });
      },

      updateDraft: (composeId, patch) => {
        set((state) => {
          const existing = state.sessions[composeId];
          if (!existing) return state;
          return {
            sessions: {
              ...state.sessions,
              [composeId]: {
                ...existing,
                draft: { ...existing.draft, ...patch },
                updatedAt: Date.now(),
              },
            },
          };
        });
      },

      setCompact: (composeId, compact) => {
        set((state) => {
          const existing = state.sessions[composeId];
          if (!existing) return state;
          return {
            sessions: {
              ...state.sessions,
              [composeId]: { ...existing, compact, updatedAt: Date.now() },
            },
            // Expanding (compact -> false) implies activating; collapsing leaves
            // activeComposeId untouched so the user can shrink without losing focus.
            activeComposeId: !compact ? composeId : state.activeComposeId,
          };
        });
      },
    }),
    {
      name: "email-compose-sessions",
      // Deferred storage: stringify + IDB write happen in `requestIdleCallback`
      // after a short debounce, so minimize/close/openCompose return
      // immediately instead of blocking on serialization of sessions that
      // can include large email bodies + base64 attachments.
      storage: createDeferredPersistStorage<Pick<EmailComposeSessionsStore, "sessions">>(),
      // `activeComposeId` is intentionally not persisted — rehydrate always
      // resets it to null (see `merge`), so writing it every focus/minimize
      // toggle would just waste serialization.
      partialize: (state) => ({ sessions: state.sessions }),
      // On rehydrate: don't auto-focus any session (better UX — user sees pills
      // for prior drafts and clicks the one they want).
      merge: (persisted, current) => {
        const p = persisted as
          | { sessions?: Record<string, ComposeSessionState>; activeComposeId?: string | null }
          | undefined;
        return {
          ...current,
          sessions: p?.sessions ?? {},
          activeComposeId: null,
        };
      },
    }
  )
);

/** Convenience hook returning selectors + actions. */
export function useEmailComposeSessions() {
  const sessions = useEmailComposeSessionsStore((s) => s.sessions);
  const activeComposeId = useEmailComposeSessionsStore((s) => s.activeComposeId);
  const openCompose = useEmailComposeSessionsStore((s) => s.openCompose);
  const focusCompose = useEmailComposeSessionsStore((s) => s.focusCompose);
  const minimizeActive = useEmailComposeSessionsStore((s) => s.minimizeActive);
  const minimizeCompose = useEmailComposeSessionsStore((s) => s.minimizeCompose);
  const closeCompose = useEmailComposeSessionsStore((s) => s.closeCompose);
  const updateDraft = useEmailComposeSessionsStore((s) => s.updateDraft);
  const setCompact = useEmailComposeSessionsStore((s) => s.setCompact);

  return {
    sessions,
    activeComposeId,
    activeSession: activeComposeId ? sessions[activeComposeId] : undefined,
    openCompose,
    focusCompose,
    minimizeActive,
    minimizeCompose,
    closeCompose,
    updateDraft,
    setCompact,
  };
}

/**
 * A compose window and the main window both hold this store; a draft started in one has to exist in the other.
 *
 * See `syncPersistedRecordAcrossWindows` — this store persists to IndexedDB, which announces
 * nothing across windows.
 */
syncPersistedRecordAcrossWindows<ComposeSessionState>({
  signalKey: "email-compose-sessions:sync",
  select: (state) => (state as EmailComposeSessionsStore).sessions,
  apply: (id, value) =>
    useEmailComposeSessionsStore.setState((state) => {
      if (!value) {
        const { [id]: _removed, ...sessions } = state.sessions;
        return { sessions };
      }
      return { sessions: { ...state.sessions, [id]: value } };
    }),
  subscribe: (listener) => {
    useEmailComposeSessionsStore.subscribe(listener);
  },
});
